import React from 'react';
import { useTranslation } from 'react-i18next';

import Button from '../../../../components/Button';
import Popup from 'reactjs-popup';
import styles from '../../../../components/modals/modals.module.scss';
import { ReactComponent as CloseIcon } from '../../../../assets/images/icons/close.svg';
import { DateTime } from 'luxon';
import SelectShipDateModal from './SelectShipDateModal';
import { BillingSubscription } from '../SplashPage';
import classNames from 'classnames';
import { supplementsManagement as events } from '../../../../lib/analytics/events';
import { getNumberSuffix } from '../../util/helpers';

interface ISkipShipmentModalProps {
  handleSelectDate: (date: string) => void;
  trigger: React.ReactElement;
  subscription: BillingSubscription;
}

export default function SkipShipmentModal({ handleSelectDate, trigger, subscription }: ISkipShipmentModalProps) {
  const { t } = useTranslation();
  const latestSkipDate = DateTime.fromISO(subscription.supplementShipmentDates!.latestSkipDate);
  const previewSkippingShipmentDate = DateTime.fromISO(
    subscription.supplementShipmentDates!.previewSkippingShipmentDate,
  );

  // If skipping the next shipment will mean the shipment goes past the skip deadline, surface modal that says user is unable to skip
  const canSkip = previewSkippingShipmentDate <= latestSkipDate;

  function expectedDeliveryDateText() {
    const threeDaysOut = previewSkippingShipmentDate.plus({ days: 3 }).toFormat('M/dd');
    const fiveDaysOut = previewSkippingShipmentDate.plus({ days: 5 }).toFormat('M/dd');
    return t('delivery_expected_date_range', { startDate: threeDaysOut, endDate: fiveDaysOut });
  }

  return (
    <Popup
      className="s3-popup"
      trigger={trigger}
      modal
      closeOnDocumentClick={false}
      onOpen={() => {
        events.skipShipmentTapped({ subscriptionId: subscription.id });
        if (!canSkip) {
          events.unableToSkipViewed({ subscriptionId: subscription.id });
        }
      }}
    >
      {(close: () => void) =>
        canSkip ? (
          <div className={styles.container}>
            <div className={classNames(styles.body, styles.shrink)}>
              <div className={styles.header}>
                <h3>{t('skip_your_upcoming_shipment')}</h3>
                <CloseIcon
                  onClick={() => {
                    events.modalCloseEvent({}, 'Skip Shipment');
                    close();
                  }}
                />
              </div>
              <hr className={styles.divider} />
              <p>{t('this_will_update_your_scheduled_shipment_date')}</p>
              <div className={styles.highlight}>
                <div className={styles.title}>{previewSkippingShipmentDate.toFormat('cccc, LLL d')}</div>
                <div className={styles.subtitle}>{expectedDeliveryDateText()}</div>
              </div>
            </div>
            <div className={classNames(styles.actions, styles.shrink)}>
              <Button
                className={styles.button}
                onClick={() => {
                  events.defaultSkipAccepted({ subscriptionId: subscription.id });
                  handleSelectDate(previewSkippingShipmentDate.toISO());
                }}
              >
                {t('update_shipment_date')}
              </Button>
              <SelectShipDateModal
                trigger={
                  <Button className={styles.button} tertiary>
                    {t('choose_different_shipment_date')}
                  </Button>
                }
                onContinue={(date: string) => handleSelectDate(date)}
                subscription={subscription}
                referrer="Skip Shipment"
              />
            </div>
          </div>
        ) : (
          <div className={styles.container}>
            <div className={styles.body}>
              <div className={styles.header}>
                <h3>{t('unable_to_skip')}</h3>
                <CloseIcon
                  onClick={() => {
                    events.modalCloseEvent({}, 'Unable to Skip');
                    close();
                  }}
                />
              </div>
              <p>{t('youve_reached_the_limit_for_modifying_your_delivery_schedule_for_your_supplements')}</p>
              <p>
                {t('while_you_cannot_skip_any_more_shipments_you_can_adjust_the_delivery_date_up_to_date_if_needed', {
                  date: `${latestSkipDate.toFormat('LLLL d')}${getNumberSuffix(latestSkipDate.day.toString(), t)}`,
                })}
              </p>
            </div>
            <div className={classNames(styles.actions, styles.shrink)}>
              <SelectShipDateModal
                trigger={<Button className={styles.button}>{t('update_shipment_date')}</Button>}
                onContinue={(date: string) => handleSelectDate(date)}
                subscription={subscription}
                referrer="Skip shipment > Unable to skip"
              />
              <Button
                className={styles.button}
                tertiary
                onClick={() => {
                  events.modalCloseEvent({}, 'Unable to Skip');
                  close();
                }}
              >
                {t('close')}
              </Button>
            </div>
          </div>
        )
      }
    </Popup>
  );
}
